import React from 'react';
import { Category } from '@graphql/generated-base';
import { Flex } from '@withjoy/joykit';
import wrapHtmlWithSafeLink from '@shared/components/SafeLink/wrapHtmlWithSafeLink';
import { ensureHtml } from '@shared/utils/html';
import { sanitizeRichTextHtml } from '@shared/utils/sanitizeHtmlContent';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';

type RichTextComponentData = Readonly<{
  id: string;
  text: string;
}>;

type RichTextProps = Readonly<{
  isBrannanLayout?: boolean;
  richTextComponentData: RichTextComponentData;
  textAlign?: React.CSSProperties['textAlign'];
  // Sanitize the HTML before injecting it. Opt-in for surfaces whose content may include legacy
  // plain-textarea values starting with HTML (e.g. the accommodations note) — editor-authored
  // content is already sanitized on input, so other callers leave this off. `sanitizeRichTextHtml`
  // keeps the editor's `text-align` styling and strips XSS vectors. Kept behind this lazy chunk so
  // `xss` never lands in an eager layout bundle.
  sanitize?: boolean;
}>;

const RichTextWithSafeLinkBase = ({ id, text, sanitize }: { id: string; text: string; sanitize?: boolean }) => {
  const html = ensureHtml(text);
  return (
    <Flex
      width="100%"
      className="jodit-editor"
      data-richtext-id={id}
      flexDirection="column"
      dangerouslySetInnerHTML={{ __html: sanitize ? sanitizeRichTextHtml(html) : html }}
    />
  );
};

const RichTextWithSafeLink = wrapHtmlWithSafeLink(RichTextWithSafeLinkBase);

const typographyOverride: OverrideTypography = [
  { variant: 'display4', category: Category.HEADING },
  { variant: 'body4', category: Category.PARAGRAPH },
];

export const RichText: React.FC<RichTextProps> = ({ richTextComponentData, textAlign = 'left', sanitize }) => {
  return (
    <GuestSiteTypographyOverride override={typographyOverride}>
      <Flex
        textAlign={textAlign}
        width="100%"
        maxWidth="100%"
        margin="0 auto"
      >
        <RichTextWithSafeLink
          id={richTextComponentData.id}
          text={richTextComponentData.text || ''}
          sanitize={sanitize}
        />
      </Flex>
    </GuestSiteTypographyOverride>
  );
};
